import { Fragment, useEffect, useState, useRef } from "react";
import Sidebar from "../Dashboard/Sidebar";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { createNewPatient } from "../../actions/patientActions";
import { clearError, clearPatientCreated } from "../../slices/patientSlice";
import { toast } from "react-toastify";
import calculateAgeFromDOB from "../../utils/calculateAge";
import AppDatePicker from "../common/AppDatePicker";

export default function NewPatient() {

    const [name, setName] = useState("");
    const [gender, setGender] = useState("");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [remarks, setRemarks] = useState("");
    const category = "Out Patient";
    const doa = "";
    const [dob, setDob] = useState("");

    const calculatedAge = calculateAgeFromDOB(dob);

    const [images, setImages] = useState([]);
    const [imagesPreview, setImagesPreview] = useState([]);

    const fileInputRef = useRef(null); // ✅ FIX

    const { loading, isPatientCreated, error } = useSelector(state => state.patientState);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const genders = ['Male', 'Female'];

    // 📌 Handle file change
    const onImagesChange = (e) => {
        const files = Array.from(e.target.files);

        files.forEach(file => {
            if (file.type.startsWith("image")) {
                const reader = new FileReader();

                reader.onload = () => {
                    if (reader.readyState === 2) {
                        setImagesPreview(old => [...old, reader.result]);
                        setImages(old => [...old, file]);
                    }
                };

                reader.readAsDataURL(file);
            } else {
                setImagesPreview(old => [...old, "pdf"]);
                setImages(old => [...old, file]);
            }
        });
    };

    // 📌 Remove file
    const removeImageHandler = (index) => {
        const updatedImages = [...images];
        const updatedPreview = [...imagesPreview];

        updatedImages.splice(index, 1);
        updatedPreview.splice(index, 1);

        setImages(updatedImages);
        setImagesPreview(updatedPreview);

        // ✅ Reset input when empty
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

    };

    // 📌 Submit
    const submitHandler = (e) => {
        e.preventDefault();

        if (!category || !category.trim()) {
            toast.error("Category is required");
            return;
        }

        if (!name || !name.trim()) {
            toast.error("Please enter patient Name");
            return;
        }

        if (!dob) {
            toast.error("Please select Date of Birth (DOB)");
            return;
        }

        if (!gender || !gender.trim()) {
            toast.error("Please select Gender");
            return;
        }

        if (!phone || !phone.trim()) {
            toast.error("Please enter Phone number");
            return;
        }

        if (!address || !address.trim()) {
            toast.error("Please enter Address");
            return;
        }

        if (!remarks || !remarks.trim()) {
            toast.error("Please enter Remarks / Diagnosis");
            return;
        }

        const formData = new FormData();

        formData.append('name', name.trim());
        formData.append('dob', dob);
        formData.append('age', calculatedAge);
        formData.append('gender', gender.trim());
        formData.append('phone', phone.trim());
        formData.append('address', address.trim().toUpperCase());
        formData.append('remarks', remarks.trim());
        formData.append('category', category.trim());
        formData.append('doa', doa);

        images.forEach(img => {
            formData.append('images', img);
        });

        dispatch(createNewPatient(formData));

        // ✅ Clear after submit
        setImages([]);
        setImagesPreview([]);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // 📌 Clear state on mount and unmount to allow repeating the page
    useEffect(() => {
        dispatch(clearPatientCreated());
        dispatch(clearError());
        return () => {
            dispatch(clearPatientCreated());
            dispatch(clearError());
        };
    }, [dispatch]);

    // 📌 Effects
    useEffect(() => {
        if (isPatientCreated) {
            toast('Patient Created Successfully!', { type: 'success' });
            dispatch(clearPatientCreated());
            navigate('/patients');
        }

        if (error) {
            toast(error, { type: 'error' });
            dispatch(clearError());
        }
    }, [isPatientCreated, error, dispatch, navigate]);

    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
                <Fragment>
                    <div className="container my-4">
                        <form onSubmit={submitHandler} className="card shadow p-4">

                            <h3 className="mb-4">Create New Patient</h3>

                            {/* CATEGORY (Out Patient only for now) */}
                            <div className="form-group mb-3">
                                <label className="fw-semibold">
                                    Category <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select bg-light"
                                    value={category}
                                    disabled
                                    required
                                >
                                    <option value="Out Patient">Out Patient (OP)</option>
                                </select>
                                <small className="text-muted">Only Out Patient is available now. In Patient registration will be added later.</small>
                            </div>

                            {/* COMMON FIELDS */}
                            {category && (
                                <>
                                    <div className="form-group mb-3">
                                        <label className="fw-semibold">
                                            Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            className="form-control"
                                            value={name}
                                            placeholder="Enter patient full name"
                                            required
                                            onChange={e => setName(e.target.value.toUpperCase())}
                                        />
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="fw-semibold">
                                            Date of Birth (DOB) <span className="text-danger">*</span>
                                            {calculatedAge !== "" && (
                                                <span className="text-success ms-2 fw-bold">
                                                    (Calculated Age: {calculatedAge} {calculatedAge === 1 ? 'year' : 'years'})
                                                </span>
                                            )}
                                        </label>
                                        <AppDatePicker
                                            label="Date of Birth (DOB)"
                                            value={dob}
                                            onChange={(val) => setDob(val)}
                                            required
                                        />
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="fw-semibold">
                                            Gender <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-control"
                                            value={gender}
                                            required
                                            onChange={e => setGender(e.target.value)}
                                        >
                                            <option value="">-- Select Gender --</option>
                                            {genders.map(g => (
                                                <option key={g} value={g}>{g}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="fw-semibold">
                                            Phone <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            value={phone}
                                            placeholder="Enter contact number"
                                            required
                                            onChange={e => setPhone(e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="fw-semibold">
                                            Address <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={address}
                                            placeholder="Enter residential address"
                                            style={{ textTransform: 'uppercase' }}
                                            required
                                            onChange={e => setAddress(e.target.value.toUpperCase())}
                                        />
                                    </div>

                                    <div className="form-group mb-3">
                                        <label className="fw-semibold">
                                            Remarks / Medical Info <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            required
                                            placeholder="e.g. Back pain, Lower spine stiffness, Diabetes"
                                            value={remarks}
                                            onChange={e => setRemarks(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            {/* IN PATIENT (Will be added in future update) */}

                            {/* FILE UPLOAD */}
                            {category && (
                                <div className="form-group mb-3">
                                    <label className="fw-semibold">
                                        Upload Reports (Images / PDF) <span className="text-muted small fw-normal">(Optional)</span>
                                    </label>

                                    <input
                                        type="file"
                                        id="fileUpload"
                                        multiple
                                        accept="image/*,application/pdf"
                                        onChange={onImagesChange}
                                        ref={fileInputRef}
                                        hidden
                                    />

                                    <label htmlFor="fileUpload" className="btn btn-outline-primary w-100">
                                        Choose Files
                                    </label>


                                    {/* Preview */}
                                    <div className="mt-3">
                                        {imagesPreview.map((item, index) => {
                                            const fileName = images[index]?.name || "File";

                                            return (
                                                <div
                                                    key={index}
                                                    title={fileName}   // ✅ HOVER NAME
                                                    style={{
                                                        display: "inline-block",
                                                        width: "70px",
                                                        height: "70px",
                                                        position: "relative",
                                                        marginRight: "10px",
                                                        marginBottom: "10px",
                                                        border: "1px solid #ddd",
                                                        borderRadius: "6px",
                                                        overflow: "hidden",
                                                        textAlign: "center",
                                                        cursor: "pointer"
                                                    }}
                                                >

                                                    {/* DELETE */}
                                                    <span
                                                        onClick={() => removeImageHandler(index)}
                                                        style={{
                                                            position: "absolute",
                                                            top: "2px",
                                                            right: "5px",
                                                            cursor: "pointer",
                                                            color: "red",
                                                            fontWeight: "bold",
                                                            background: "#fff",
                                                            borderRadius: "50%",
                                                            padding: "0 5px",
                                                            fontSize: "12px",
                                                            zIndex: 2
                                                        }}
                                                    >
                                                        ✖
                                                    </span>

                                                    {/* PREVIEW */}
                                                    {item === "pdf" ? (
                                                        <div
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "22px"
                                                            }}
                                                        >
                                                            📄
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={item}
                                                            alt="preview"
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover"
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                </div>
                            )}

                            {/* SUBMIT */}
                            <button
                                type="submit"
                                className="btn btn-primary w-100 py-2 mt-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Creating Patient...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-user-plus me-2"></i>CREATE PATIENT (OUT PATIENT)
                                    </>
                                )}
                            </button>

                        </form>
                    </div>
                </Fragment>
        </div>
        </div>
    );
}



